import { useState } from "react";
import { SERVER_ORIGIN } from "../lib/config";
import { play } from "../lib/sound";
import { ParentalGate } from "./ParentalGate";
import { QrCode } from "./QrCode";
import { Button } from "./ui";

interface Props {
	code: string;
	/** Shown alongside the code while a friend is still missing. */
	compact?: boolean;
}

export function inviteLink(code: string): string {
	return `${SERVER_ORIGIN || "https://playpal.app"}/?room=${code}`;
}

/** Copy, share sheet and QR — three ways to get a friend into the room. */
export function ShareRow({ code, compact = false }: Props) {
	const [showQr, setShowQr] = useState(!compact);
	const [note, setNote] = useState<string | null>(null);
	// The share sheet is the only way out of this app into another one, which is
	// exactly what Apple requires a Kids Category app to gate. Copying the code
	// and showing the QR both stay on the device, so neither needs one.
	const [askingGrownUp, setAskingGrownUp] = useState(false);
	const link = inviteLink(code);

	const flash = (message: string) => {
		setNote(message);
		setTimeout(() => setNote(null), 1800);
	};

	const share = async () => {
		const payload = {
			title: "PlayPal",
			text: `Come and play! My code is ${code}.`,
			url: link,
		};
		try {
			if (navigator.share) {
				await navigator.share(payload);
				return;
			}
			await navigator.clipboard.writeText(link);
			flash("Link copied!");
		} catch (error) {
			// A cancelled share sheet is not a failure worth shouting about.
			if (error instanceof Error && error.name === "AbortError") return;
			flash("Couldn't share — read the code out instead.");
		}
	};

	const copyCode = async () => {
		try {
			await navigator.clipboard.writeText(code);
			flash("Code copied!");
		} catch {
			flash("Couldn't copy — read it out instead.");
		}
	};

	return (
		<div className="flex flex-col items-center gap-4">
			{askingGrownUp && (
				<ParentalGate
					action="Sharing the invite opens another app."
					onPass={() => {
						setAskingGrownUp(false);
						void share();
					}}
					onCancel={() => setAskingGrownUp(false)}
				/>
			)}

			<div className="flex flex-wrap justify-center gap-2">
				<Button
					onClick={() => {
						play("tap");
						setAskingGrownUp(true);
					}}
				>
					📤 Share link
				</Button>
				<Button tone="secondary" onClick={() => void copyCode()}>
					📋 Copy code
				</Button>
				<Button tone="secondary" onClick={() => setShowQr((open) => !open)} aria-expanded={showQr}>
					{showQr ? "🙈 Hide QR" : "📱 Show QR"}
				</Button>
			</div>

			{showQr && (
				<div className="flex flex-col items-center gap-2">
					<QrCode value={link} size={compact ? 160 : 220} label={`Scan to join room ${code}`} />
					<p className="text-sm text-indigo-900/70 dark:text-indigo-100/70">
						Point your friend's camera at this
					</p>
				</div>
			)}

			<p role="status" className="h-5 text-sm font-semibold">
				{note}
			</p>
		</div>
	);
}
